/**
 * DATA LAYER (DL): ARMA LA PLAYLIST [HU-04]
 * Capa de integración HTTP REST para la dinámica 'Arma la Playlist' (Admin / Operador / Aficionado / Pantalla).
 * Maneja peticiones contra los endpoints '/api/v1/playlist' con fallback a '/api/playlist'.
 * 
 * @module utilities/DL/Admin/Overlays/Playlist
 */

/**
 * Construye la URL base para el módulo de Playlist admitiendo variables de entorno dinámicas.
 * Soporta 'v1/playlist' con fallback a 'playlist'.
 * 
 * @param {string} [subPath=''] - Sub-ruta o endpoint específico.
 * @returns {string} URL completa formateada.
 */
const getEndpointUrl = (subPath = '') => {
    let base = process.env.REACT_APP_API_URL || 'https://localhost:50586/api/';
    if (!base.endsWith('/')) {
        base += '/';
    }

    const cleanSub = subPath.startsWith('/') ? subPath.slice(1) : subPath;
    
    // Determinar prefijo según si base ya incluye 'api/'
    let prefix = 'v1/playlist';
    if (!base.includes('/api/')) {
        prefix = 'api/v1/playlist';
    }

    return cleanSub ? `${base}${prefix}/${cleanSub}` : `${base}${prefix}`;
};

/**
 * Retorna los headers estándar requeridos para las peticiones HTTP,
 * incluyendo Bearer token y el identificador de sala en header 'X-Room-Id'.
 * 
 * @param {string|null} [roomId=null] - Identificador GUID de la sala.
 * @param {boolean} [isJson=true] - Si true, incluye Content-Type: application/json.
 * @returns {Object} Diccionario de headers HTTP.
 */
const getAuthHeaders = (roomId = null, isJson = true) => {
    const token = localStorage.getItem('interaktia_token');
    const headers = {};

    if (isJson) {
        headers['Content-Type'] = 'application/json';
    }

    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    if (roomId) {
        headers['X-Room-Id'] = String(roomId);
    }

    return headers;
};

/**
 * Procesa la respuesta de la API de forma unificada.
 * 
 * @param {Response} response - Respuesta fetch nativa.
 * @returns {Promise<{success: boolean, data: any, message: string}>}
 */
const handleApiResponse = async (response) => {
    try {
        if (!response.ok) {
            let errorMessage = `Error HTTP ${response.status}: ${response.statusText}`;
            try {
                const errorData = await response.json();
                if (errorData && errorData.message) {
                    errorMessage = errorData.message;
                }
            } catch (_) {}
            return { success: false, data: null, message: errorMessage };
        }

        const data = await response.json();
        return {
            success: data.success !== undefined ? data.success : true,
            data: data.data !== undefined ? data.data : data,
            message: data.message || ''
        };
    } catch (error) {
        return {
            success: false,
            data: null,
            message: error.message || 'Error al procesar la respuesta del servidor.'
        };
    }
};

/**
 * Obtiene la lista filtrada de sugerencias de una sala para la consola de administración/cabina.
 * 
 * @param {Object} params
 * @param {string} [params.roomId] - GUID de la sala.
 * @param {boolean} [params.includeArchived=false] - Incluir canciones archivadas.
 * @param {boolean} [params.groupSimilar=false] - Agrupar sugerencias similares.
 * @param {number|string} [params.view] - Vista por defecto (0: Recientes, 1: Más votadas, 2: Favoritas, 3: Historial).
 * @param {boolean} [params.isFavorite] - Filtrar por favoritas.
 * @param {boolean} [params.isPlayed] - Filtrar por reproducidas.
 * @param {string} [params.search] - Búsqueda por título o artista.
 */
export const GetSuggestions = async ({
    roomId,
    includeArchived = false,
    groupSimilar = false,
    view = null,
    isFavorite = null,
    isPlayed = null,
    search = null
} = {}) => {
    try {
        const query = new URLSearchParams();
        if (roomId) query.append('roomId', roomId);
        if (includeArchived !== undefined && includeArchived !== null) query.append('includeArchived', includeArchived);
        if (groupSimilar !== undefined && groupSimilar !== null) query.append('groupSimilar', groupSimilar);
        if (view !== null && view !== undefined) query.append('view', view);
        if (isFavorite !== null && isFavorite !== undefined) query.append('isFavorite', isFavorite);
        if (isPlayed !== null && isPlayed !== undefined) query.append('isPlayed', isPlayed);
        if (search) query.append('search', search);

        const url = `${getEndpointUrl('suggestions')}${query.toString() ? `?${query.toString()}` : ''}`;
        const response = await fetch(url, {
            method: 'GET',
            headers: getAuthHeaders(roomId)
        });

        return await handleApiResponse(response);
    } catch (error) {
        return { success: false, data: null, message: error.message || 'Error de red al consultar sugerencias.' };
    }
};

/**
 * Obtiene la lista activa ordenada de la playlist (cola de reproducción).
 * 
 * @param {Object} params
 * @param {string} [params.roomId] - GUID de la sala.
 */
export const GetActivePlaylist = async (params = {}) => {
    try {
        const roomId = typeof params === 'string' ? params : (params?.roomId || null);
        const query = new URLSearchParams();
        if (roomId) query.append('roomId', roomId);

        const url = `${getEndpointUrl('active')}${query.toString() ? `?${query.toString()}` : ''}`;
        const response = await fetch(url, {
            method: 'GET',
            headers: getAuthHeaders(roomId)
        });

        return await handleApiResponse(response);
    } catch (error) {
        return { success: false, data: null, message: error.message || 'Error de red al consultar la playlist activa.' };
    }
};

/**
 * Envía una sugerencia musical de un aficionado.
 * 
 * @param {Object} params
 * @param {string} params.roomId - GUID de la sala.
 * @param {number} [params.suggestionType=0] - 0: Canción, 1: Artista.
 * @param {string} params.title - Título o descripción de la sugerencia.
 * @param {string} [params.artistName] - Artista.
 * @param {string} [params.fanName] - Nombre del aficionado.
 * @param {string} [params.fanSessionId] - Cookie o identificador de sesión del fan.
 * @param {string} [params.clientIpAddress] - IP del cliente.
 */
export const SubmitSuggestion = async ({
    roomId,
    suggestionType = 0,
    title,
    artistName = '',
    fanName = '',
    fanSessionId = '',
    clientIpAddress = ''
} = {}) => {
    try {
        const query = new URLSearchParams();
        if (roomId) query.append('roomId', roomId);

        const payload = {
            roomId: roomId || '00000000-0000-0000-0000-000000000000',
            suggestionType: Number(suggestionType) || 0,
            title: title || '',
            artistName: artistName || '',
            fanName: fanName || '',
            fanSessionId: fanSessionId || '',
            clientIpAddress: clientIpAddress || ''
        };

        const url = `${getEndpointUrl('suggest')}${query.toString() ? `?${query.toString()}` : ''}`;
        const response = await fetch(url, {
            method: 'POST',
            headers: getAuthHeaders(roomId, true),
            body: JSON.stringify(payload)
        });

        return await handleApiResponse(response);
    } catch (error) {
        return { success: false, data: null, message: error.message || 'Error de red al enviar la sugerencia.' };
    }
};

/**
 * Crea una canción manual agregada por el DJ u operador.
 * 
 * @param {Object} params
 * @param {string} params.roomId - GUID de la sala.
 * @param {number} [params.suggestionType=0] - 0: Canción, 1: Artista.
 * @param {string} params.title - Título de la pista.
 * @param {string} [params.artistName] - Nombre del artista.
 * @param {string} [params.fanName] - Nombre referencial.
 * @param {string} [params.operatorName] - Nombre del operador o DJ.
 * @param {boolean} [params.isFavorite=false] - Marcar inmediatamente como favorita/pin.
 */
export const CreateManualSong = async ({
    roomId,
    suggestionType = 0,
    title,
    artistName = '',
    fanName = '',
    operatorName = '',
    isFavorite = false
} = {}) => {
    try {
        const query = new URLSearchParams();
        if (roomId) query.append('roomId', roomId);

        const payload = {
            roomId: roomId || '00000000-0000-0000-0000-000000000000',
            suggestionType: Number(suggestionType) || 0,
            title: title || '',
            artistName: artistName || '',
            operatorName: operatorName || fanName || 'Operador',
            isFavorite: Boolean(isFavorite)
        };

        const url = `${getEndpointUrl('manual')}${query.toString() ? `?${query.toString()}` : ''}`;
        const response = await fetch(url, {
            method: 'POST',
            headers: getAuthHeaders(roomId, true),
            body: JSON.stringify(payload)
        });

        return await handleApiResponse(response);
    } catch (error) {
        return { success: false, data: null, message: error.message || 'Error de red al crear la canción manual.' };
    }
};

/**
 * Alterna o actualiza el estado de favorito (pin) de una sugerencia.
 * 
 * @param {Object} params
 * @param {string} [params.roomId] - GUID de la sala.
 * @param {string} params.suggestionId - GUID de la sugerencia.
 * @param {boolean} [params.isFavorite] - Estado explícito o alternar si se omite.
 */
export const ToggleFavorite = async ({
    roomId,
    suggestionId,
    isFavorite
} = {}) => {
    try {
        const query = new URLSearchParams();
        if (roomId) query.append('roomId', roomId);

        const url = `${getEndpointUrl(`suggestion/${suggestionId}/favorite`)}${query.toString() ? `?${query.toString()}` : ''}`;
        const body = isFavorite !== undefined && isFavorite !== null ? JSON.stringify({ isFavorite }) : JSON.stringify({});

        const response = await fetch(url, {
            method: 'PUT',
            headers: getAuthHeaders(roomId, true),
            body: body
        });

        return await handleApiResponse(response);
    } catch (error) {
        return { success: false, data: null, message: error.message || 'Error de red al actualizar favorito.' };
    }
};

/**
 * Alterna o actualiza el estado de archivado de una sugerencia.
 * 
 * @param {Object} params
 * @param {string} [params.roomId] - GUID de la sala.
 * @param {string} params.suggestionId - GUID de la sugerencia.
 * @param {boolean} [params.isArchive] - Alias de isArchived.
 * @param {boolean} [params.isArchived] - Estado explícito de archivado.
 */
export const ToggleArchive = async ({
    roomId,
    suggestionId,
    isArchive,
    isArchived
} = {}) => {
    try {
        const query = new URLSearchParams();
        if (roomId) query.append('roomId', roomId);

        const targetArchived = isArchived !== undefined ? isArchived : isArchive;
        const url = `${getEndpointUrl(`suggestion/${suggestionId}/archive`)}${query.toString() ? `?${query.toString()}` : ''}`;
        const body = targetArchived !== undefined && targetArchived !== null ? JSON.stringify({ isArchived: targetArchived }) : JSON.stringify({});

        const response = await fetch(url, {
            method: 'PUT',
            headers: getAuthHeaders(roomId, true),
            body: body
        });

        return await handleApiResponse(response);
    } catch (error) {
        return { success: false, data: null, message: error.message || 'Error de red al actualizar estado de archivado.' };
    }
};

/**
 * Actualiza campos específicos de una sugerencia (título, artista, fan, orden, etc.).
 * 
 * @param {Object} params
 * @param {string} [params.roomId] - GUID de la sala.
 * @param {string} params.suggestionId - GUID de la sugerencia.
 * @param {string} [params.title] - Nuevo título.
 * @param {string} [params.artistName] - Nuevo nombre de artista.
 * @param {string} [params.fanName] - Nuevo nombre del aficionado.
 * @param {boolean} [params.isFavorite] - Estado favorito.
 * @param {boolean} [params.isArchived] - Estado archivado.
 * @param {boolean} [params.isPlayed] - Estado reproducida.
 * @param {boolean} [params.isNowPlaying] - Estado Now Playing.
 * @param {number} [params.orderIndex] - Orden en playlist.
 * @param {number} [params.suggestionType] - Tipo de sugerencia.
 */
export const UpdateSuggestion = async ({
    roomId,
    suggestionId,
    title,
    artistName,
    fanName,
    isFavorite,
    isArchived,
    isPlayed,
    isNowPlaying,
    orderIndex,
    suggestionType
} = {}) => {
    try {
        const query = new URLSearchParams();
        if (roomId) query.append('roomId', roomId);

        const payload = {};
        if (title !== undefined) payload.title = title;
        if (artistName !== undefined) payload.artistName = artistName;
        if (fanName !== undefined) payload.fanName = fanName;
        if (isFavorite !== undefined) payload.isFavorite = isFavorite;
        if (isArchived !== undefined) payload.isArchived = isArchived;
        if (isPlayed !== undefined) payload.isPlayed = isPlayed;
        if (isNowPlaying !== undefined) payload.isNowPlaying = isNowPlaying;
        if (orderIndex !== undefined) payload.orderIndex = orderIndex;
        if (suggestionType !== undefined) payload.suggestionType = suggestionType;

        const url = `${getEndpointUrl(`suggestion/${suggestionId}`)}${query.toString() ? `?${query.toString()}` : ''}`;
        const response = await fetch(url, {
            method: 'PUT',
            headers: getAuthHeaders(roomId, true),
            body: JSON.stringify(payload)
        });

        return await handleApiResponse(response);
    } catch (error) {
        return { success: false, data: null, message: error.message || 'Error de red al actualizar la sugerencia.' };
    }
};

/**
 * Elimina una sugerencia de la sala (soft delete por defecto o eliminación física).
 * 
 * @param {Object} params
 * @param {string} [params.roomId] - GUID de la sala.
 * @param {string} params.suggestionId - GUID de la sugerencia a eliminar.
 * @param {boolean} [params.softDelete=true] - Soft delete o borrado definitivo.
 */
export const DeleteSuggestion = async ({
    roomId,
    suggestionId,
    softDelete = true
} = {}) => {
    try {
        const query = new URLSearchParams();
        if (roomId) query.append('roomId', roomId);
        if (softDelete !== undefined) query.append('softDelete', softDelete);

        const url = `${getEndpointUrl(`suggestion/${suggestionId}`)}${query.toString() ? `?${query.toString()}` : ''}`;
        const response = await fetch(url, {
            method: 'DELETE',
            headers: getAuthHeaders(roomId)
        });

        return await handleApiResponse(response);
    } catch (error) {
        return { success: false, data: null, message: error.message || 'Error de red al eliminar la sugerencia.' };
    }
};

/**
 * Reordena la cola activa de canciones.
 * 
 * @param {Object} params
 * @param {string} [params.roomId] - GUID de la sala.
 * @param {Array<string>} params.orderedIds - Lista ordenada de GUIDs de sugerencias.
 */
export const ReorderPlaylist = async ({
    roomId,
    orderedIds = []
} = {}) => {
    try {
        const query = new URLSearchParams();
        if (roomId) query.append('roomId', roomId);

        const payload = {
            orderedSuggestionIds: orderedIds
        };

        const url = `${getEndpointUrl('reorder')}${query.toString() ? `?${query.toString()}` : ''}`;
        const response = await fetch(url, {
            method: 'PUT',
            headers: getAuthHeaders(roomId, true),
            body: JSON.stringify(payload)
        });

        return await handleApiResponse(response);
    } catch (error) {
        return { success: false, data: null, message: error.message || 'Error de red al reordenar la playlist.' };
    }
};

/**
 * Establece o remueve la canción que está sonando en vivo (Now Playing).
 * 
 * @param {Object} params
 * @param {string} [params.roomId] - GUID de la sala.
 * @param {string|null} [params.suggestionId=null] - GUID de la sugerencia (o null para limpiar).
 */
export const SetNowPlaying = async ({
    roomId,
    suggestionId = null
} = {}) => {
    try {
        const query = new URLSearchParams();
        if (roomId) query.append('roomId', roomId);

        const payload = {
            suggestionId: suggestionId || null
        };

        const url = `${getEndpointUrl('now-playing')}${query.toString() ? `?${query.toString()}` : ''}`;
        const response = await fetch(url, {
            method: 'PUT',
            headers: getAuthHeaders(roomId, true),
            body: JSON.stringify(payload)
        });

        return await handleApiResponse(response);
    } catch (error) {
        return { success: false, data: null, message: error.message || 'Error de red al establecer Now Playing.' };
    }
};

/**
 * Alterna o actualiza el estado de reproducida (IsPlayed) de una canción.
 * 
 * @param {Object} params
 * @param {string} [params.roomId] - GUID de la sala.
 * @param {string} params.suggestionId - GUID de la sugerencia.
 * @param {boolean} [params.isPlayed] - Estado explícito o alternar si se omite.
 */
export const TogglePlayed = async ({
    roomId,
    suggestionId,
    isPlayed
} = {}) => {
    try {
        const query = new URLSearchParams();
        if (roomId) query.append('roomId', roomId);

        const url = `${getEndpointUrl(`suggestion/${suggestionId}/played`)}${query.toString() ? `?${query.toString()}` : ''}`;
        const body = isPlayed !== undefined && isPlayed !== null ? JSON.stringify({ isPlayed }) : JSON.stringify({});

        const response = await fetch(url, {
            method: 'PUT',
            headers: getAuthHeaders(roomId, true),
            body: body
        });

        return await handleApiResponse(response);
    } catch (error) {
        return { success: false, data: null, message: error.message || 'Error de red al actualizar estado de reproducción.' };
    }
};

/**
 * Obtiene la configuración operativa y visual completa de Arma la Playlist para la sala.
 * 
 * @param {Object} params
 * @param {string} [params.roomId] - GUID de la sala.
 */
export const GetSettings = async (params = {}) => {
    try {
        const roomId = typeof params === 'string' ? params : (params?.roomId || null);
        const query = new URLSearchParams();
        if (roomId) query.append('roomId', roomId);

        const url = `${getEndpointUrl('settings')}${query.toString() ? `?${query.toString()}` : ''}`;
        const response = await fetch(url, {
            method: 'GET',
            headers: getAuthHeaders(roomId)
        });

        return await handleApiResponse(response);
    } catch (error) {
        return { success: false, data: null, message: error.message || 'Error de red al consultar la configuración.' };
    }
};

export const GetPlaylistSettings = GetSettings;

/**
 * Actualiza los ajustes operativos y visuales de Arma la Playlist.
 * 
 * @param {Object} params
 * @param {string} [params.roomId] - GUID de la sala.
 * @param {Object} params.data - DTO con campos de configuración a actualizar.
 */
export const UpdateSettings = async ({ roomId, data = {} } = {}) => {
    try {
        const query = new URLSearchParams();
        if (roomId) query.append('roomId', roomId);

        const url = `${getEndpointUrl('settings')}${query.toString() ? `?${query.toString()}` : ''}`;
        const response = await fetch(url, {
            method: 'PUT',
            headers: getAuthHeaders(roomId, true),
            body: JSON.stringify(data)
        });

        return await handleApiResponse(response);
    } catch (error) {
        return { success: false, data: null, message: error.message || 'Error de red al actualizar la configuración.' };
    }
};

/**
 * Abre o pausa la recepción de sugerencias por parte de los aficionados.
 * 
 * @param {Object} params
 * @param {string} [params.roomId] - GUID de la sala.
 * @param {boolean} [params.isActive] - Estado explícito o alternar si se omite.
 */
export const ToggleSubmissionActive = async ({ roomId, isActive } = {}) => {
    try {
        const query = new URLSearchParams();
        if (roomId) query.append('roomId', roomId);

        const url = `${getEndpointUrl('submission-active')}${query.toString() ? `?${query.toString()}` : ''}`;
        const body = isActive !== undefined && isActive !== null ? JSON.stringify({ isActive }) : JSON.stringify({});

        const response = await fetch(url, {
            method: 'PUT',
            headers: getAuthHeaders(roomId, true),
            body: body
        });

        return await handleApiResponse(response);
    } catch (error) {
        return { success: false, data: null, message: error.message || 'Error de red al actualizar recepción de sugerencias.' };
    }
};

/**
 * Reinicia la dinámica en la sala (archiva sugerencias activas, limpia Now Playing y notifica en tiempo real).
 * 
 * @param {Object} params
 * @param {string} [params.roomId] - GUID de la sala.
 * @param {string} [params.confirmationPassword] - Contraseña opcional de confirmación.
 */
export const ResetDynamic = async ({ roomId, confirmationPassword } = {}) => {
    try {
        const query = new URLSearchParams();
        if (roomId) query.append('roomId', roomId);

        const payload = {
            roomId: roomId || null,
            confirmationPassword: confirmationPassword || null
        };

        const url = `${getEndpointUrl('reset')}${query.toString() ? `?${query.toString()}` : ''}`;
        const response = await fetch(url, {
            method: 'POST',
            headers: getAuthHeaders(roomId, true),
            body: JSON.stringify(payload)
        });

        return await handleApiResponse(response);
    } catch (error) {
        return { success: false, data: null, message: error.message || 'Error de red al reiniciar la dinámica.' };
    }
};

/**
 * Obtiene el resumen de métricas y estadísticas en tiempo real de Arma la Playlist.
 * 
 * @param {Object} params
 * @param {string} [params.roomId] - GUID de la sala.
 */
export const GetMetrics = async ({ roomId } = {}) => {
    try {
        const query = new URLSearchParams();
        if (roomId) query.append('roomId', roomId);

        const url = `${getEndpointUrl('metrics')}${query.toString() ? `?${query.toString()}` : ''}`;
        const response = await fetch(url, {
            method: 'GET',
            headers: getAuthHeaders(roomId)
        });

        return await handleApiResponse(response);
    } catch (error) {
        return { success: false, data: null, message: error.message || 'Error de red al consultar métricas.' };
    }
};

/**
 * Obtiene la configuración pública de Arma la Playlist para las pantallas y vista aficionado.
 * 
 * @param {Object} params
 * @param {string} [params.roomId] - GUID de la sala.
 */
export const GetPublicConfig = async ({ roomId } = {}) => {
    try {
        const query = new URLSearchParams();
        if (roomId) query.append('roomId', roomId);

        const url = `${getEndpointUrl('public-config')}${query.toString() ? `?${query.toString()}` : ''}`;
        const response = await fetch(url, {
            method: 'GET',
            headers: getAuthHeaders(roomId)
        });

        return await handleApiResponse(response);
    } catch (error) {
        return { success: false, data: null, message: error.message || 'Error de red al consultar la configuración pública.' };
    }
};

/**
 * Sube y optimiza un recurso visual o fondo para Arma la Playlist.
 * Petición POST /api/v1/playlist/upload-asset enviando multipart/form-data con Authorization y X-Room-Id.
 * 
 * @param {FormData|File} fileOrFormData - Objeto FormData o archivo File.
 * @param {string|null} [roomId=null] - GUID de la sala.
 * @returns {Promise<{success: boolean, url?: string, data?: any, message: string}>}
 */
export const UploadPlaylistAsset = async (fileOrFormData, roomId = null) => {
    try {
        let formData = fileOrFormData;
        if (!(fileOrFormData instanceof FormData)) {
            formData = new FormData();
            formData.append('file', fileOrFormData);
        }

        const query = new URLSearchParams();
        if (roomId) query.append('roomId', roomId);

        const url = `${getEndpointUrl('upload-asset')}${query.toString() ? `?${query.toString()}` : ''}`;
        const headers = getAuthHeaders(roomId, false);

        const response = await fetch(url, {
            method: 'POST',
            headers: headers,
            body: formData
        });

        const result = await handleApiResponse(response);
        return {
            success: result.success,
            url: result.data?.url || (typeof result.data === 'string' ? result.data : null),
            data: result.data,
            message: result.message || (result.success ? 'Recurso subido exitosamente.' : 'Error al subir el archivo.')
        };
    } catch (error) {
        return { success: false, url: null, data: null, message: error.message || 'Error de red al subir el recurso.' };
    }
};

export default {
    GetSuggestions,
    GetActivePlaylist,
    SubmitSuggestion,
    CreateManualSong,
    ToggleFavorite,
    ToggleArchive,
    UpdateSuggestion,
    DeleteSuggestion,
    ReorderPlaylist,
    SetNowPlaying,
    TogglePlayed,
    GetSettings,
    GetPlaylistSettings,
    UpdateSettings,
    ToggleSubmissionActive,
    ResetDynamic,
    GetMetrics,
    GetPublicConfig,
    UploadPlaylistAsset
};
